import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Search, Send, Download, UploadCloud, Loader2, RotateCw, Filter } from "lucide-react";
import { toast } from "sonner";
import { EU, MillCountryImport } from "@/components/Reimport";
import { RefReqBatch, RefReqChip } from "@/components/RefRequest";
import { ProdReqBatch, ProdReqChip } from "@/components/ProductRequest";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, download, errMsg, fmtMoney, fmtNum } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { usePlant } from "@/context/PlantContext";
import { PageHeader, StatusChip } from "@/components/common";
import DataTable, { useDebounced } from "@/components/DataTable";
import { ActionBanner } from "@/components/ActionBanner";
import { cn } from "@/lib/utils";

export const SUP_STATUS = {
  not_invited: ["Not invited", "bg-slate-100 text-slate-600 border-slate-200", "#94A3B8"],
  invited: ["Invited", "bg-sky-50 text-sky-700 border-sky-200", "#0284C7"],
  in_progress: ["In progress", "bg-indigo-50 text-indigo-700 border-indigo-200", "#6366F1"],
  submitted: ["Submitted", "bg-amber-50 text-amber-800 border-amber-200", "#D97706"],
  changes_requested: ["Changes requested", "bg-orange-50 text-orange-800 border-orange-200", "#EA580C"],
  approved: ["Approved", "bg-emerald-50 text-emerald-700 border-emerald-200", "#059669"],
  expired: ["Expired", "bg-rose-50 text-rose-700 border-rose-200", "#E11D48"],
};
export const SupStatus = ({ s, testid }) => (
  <span data-testid={testid} className={cn("inline-flex whitespace-nowrap rounded-full border px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide", SUP_STATUS[s]?.[1])}>{SUP_STATUS[s]?.[0] || s}</span>
);
const COMMODITIES = ["Cattle", "Cocoa", "Coffee", "Oil palm", "Rubber", "Soya", "Wood"];

function BatchResult({ res, onClose }) {
  return (
    <Dialog open={!!res} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-2xl" data-testid="batch-result-dialog">
        <DialogHeader><DialogTitle data-testid="batch-summary">{res?.title || `${res?.queued} emails queued, ${res?.skipped} skipped (No contact email)`}</DialogTitle></DialogHeader>
        <p className="text-xs text-slate-500">Emails are sent in the background, paced to the provider's rate limit. Any failures show as "Email failed" in the list – use Retry failed emails.</p>
        <div className="max-h-80 overflow-y-auto text-sm">
          {res?.results.map((r) => (
            <div key={r.supplier_id} className="flex items-center justify-between border-b border-slate-100 py-2">
              <span><span className="font-mono text-xs">{r.vendor}</span> {r.name}</span>
              <StatusChip status={r.result === "skipped" ? "setup" : "processing"} label={r.reason || r.result} />
            </div>
          ))}
        </div>
        <Button data-testid="batch-export-links" onClick={() => download(`/suppliers/links.csv?ids=${res.results.map((r) => r.supplier_id).join(",")}`, "supplier_links.csv")}><Download className="mr-1.5 h-4 w-4" /> Download links CSV</Button>
      </DialogContent>
    </Dialog>
  );
}

function PatternsDialog({ open, onClose, onSaved }) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) api.get("/settings/placeholder-patterns").then((r) => setText(r.data.patterns.join("\n"))); }, [open]);
  const save = async () => {
    setBusy(true);
    try {
      const r = await api.put("/settings/placeholder-patterns", { patterns: text.split("\n") });
      toast.success(`Exclusion rules saved – ${r.data.candidates} EUDR suppliers`);
      onSaved();
      onClose();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="patterns-dialog">
        <DialogHeader><DialogTitle>Placeholder vendor exclusions</DialogTitle></DialogHeader>
        <p className="text-sm text-slate-500">Vendors whose name or search term contains any of these phrases (one per line, case-insensitive) are not treated as EUDR suppliers.</p>
        <Textarea data-testid="patterns-input" rows={6} value={text} onChange={(e) => setText(e.target.value)} className="font-mono text-sm" />
        <DialogFooter><Button variant="outline" onClick={onClose}>Cancel</Button><Button data-testid="patterns-save-btn" disabled={busy} onClick={save}>Save & refresh suppliers</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Suppliers() {
  const [sp] = useSearchParams();
  const nav = useNavigate();
  const { can } = useAuth();
  const { params: gp } = usePlant();
  const urlPlant = sp.get("plant");
  const pp = useMemo(() => (urlPlant ? { plant: urlPlant } : gp), [urlPlant, gp]);
  const [q, setQ] = useState("");
  const [country, setCountry] = useState("");
  const [commodity, setCommodity] = useState("all");
  const [status, setStatus] = useState(sp.get("status") || "all");
  const [risk, setRisk] = useState("all");
  const [mill, setMill] = useState(sp.get("mill") || "all");
  const action = sp.get("action") || "";
  const [refreq, setRefreq] = useState("all");
  const [prodreq, setProdreq] = useState("all");
  const [ic, setIc] = useState(false);
  const [sel, setSel] = useState([]);
  const [auto, setAuto] = useState(false);
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState(null);
  const [reload, setReload] = useState(0);
  const [patterns, setPatterns] = useState(false);
  const retry = async () => {
    setBusy(true);
    try {
      const r = await api.post("/suppliers/retry-failed-emails");
      if (!r.data.results.length) toast.success("No failed emails to retry");
      else setRes({ ...r.data, title: `${r.data.queued} failed emails queued for retry` });
      setTimeout(() => setReload((x) => x + 1), 4000);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const fileRef = useRef();
  const dq = useDebounced(q);
  const dc = useDebounced(country);
  const params = useMemo(() => ({ ...pp, q: dq, country: dc, commodity, status, country_risk: risk, ...(mill !== "all" ? { mill } : {}), ...(refreq !== "all" ? { refreq } : {}), ...(prodreq !== "all" ? { prodreq } : {}), ...(ic ? { intercompany: true } : {}), ...(action ? { action, stock: sp.get("stock") || "in" } : {}) }), [sp, pp, dq, dc, commodity, status, risk, mill, refreq, prodreq, ic, action]);
  useEffect(() => setSel([]), [params]);
  const toggle = (id) => setSel((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  const batch = async () => {
    setBusy(true);
    try {
      const r = await api.post("/suppliers/batch-invite", { supplier_ids: sel, auto_remind: auto });
      setRes(r.data);
      setReload((x) => x + 1);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const importContacts = async (f) => {
    const fd = new FormData();
    fd.append("file", f);
    try {
      const r = await api.post("/suppliers/contacts/import", fd);
      toast.success(`${r.data.added} contacts added, ${r.data.updated} updated, ${r.data.failed.length} failed`, {
        description: r.data.failed.slice(0, 5).map((x) => `Row ${x.row}: ${x.reason}`).join(" · ") });
      setReload((x) => x + 1);
    } catch (e) { toast.error(errMsg(e)); }
  };

  const columns = [
    ...(can.upload ? [{ key: "sel", label: "", render: (r) => r.intercompany ? null : <span onClick={(e) => e.stopPropagation()}><Checkbox data-testid={`supplier-select-${r.vendor}`} checked={sel.includes(r.id)} onCheckedChange={() => toggle(r.id)} /></span> }] : []),
    { key: "vendor", label: "Vendor", sortable: true, render: (r) => <span className="font-mono text-[13px] text-slate-900">{r.vendor}</span> },
    { key: "name", label: "Supplier", sortable: true, className: "max-w-[260px] truncate", render: (r) => <span className="font-medium text-slate-800" title={r.name}>{r.name}</span> },
    { key: "country", label: "Country", sortable: true },
    { key: "mill_country", label: "Mill / dispatch", render: (r) => { const m = r.mill_country || r.country; return <span data-testid={`supplier-mill-${r.vendor}`} className={m !== r.country ? "font-semibold text-sky-700" : ""}>{m || "—"}{EU.includes(m) ? " · EU" : ""}{r.intercompany ? <span className="ml-1 rounded bg-violet-50 px-1 text-[10px] font-semibold text-violet-700">Intercompany</span> : null}{r.ref_request ? <span className="ml-1"><RefReqChip r={r.ref_request} testid={`supplier-refreq-${r.vendor}`} /></span> : null}{r.product_request ? <span className="ml-1"><ProdReqChip r={r.product_request} testid={`supplier-prodreq-${r.vendor}`} /></span> : null}</span>; } },
    { key: "status", label: "Status", sortable: true, render: (r) => <SupStatus s={r.status} testid={`supplier-status-${r.vendor}`} /> },
    { key: "material_count", label: "In-scope materials", sortable: true, className: "text-right", thClass: "text-right", render: (r) => <span data-testid={`supplier-materials-${r.vendor}`}>{r.material_count}</span> },
    { key: "spend_12m", label: "Spend 12m", sortable: true, className: "text-right", thClass: "text-right", render: (r) => fmtMoney(r.spend_12m) },
    { key: "commodities", label: "Commodity", render: (r) => r.commodities?.join(", ") || "—" },
    { key: "plants", label: "Plants", render: (r) => <span className="font-mono text-xs">{r.plants?.join(", ")}</span> },
    { key: "contacts", label: "Contact", render: (r) => r.contacts?.[0]?.email || <span className="text-xs text-slate-400">none</span> },
    { key: "email_status", label: "Email", render: (r) => r.email_status === "failed"
      ? <span className="text-xs font-medium text-rose-600" data-testid={`supplier-email-${r.vendor}`}>Email failed: {r.email_error}</span>
      : <span className="text-xs text-slate-500" data-testid={`supplier-email-${r.vendor}`}>{r.email_status?.replace("_", " ") || "—"}</span> },
    { key: "country_risk", label: "Risk", render: (r) => r.country_risk || "—" },
  ];

  return (
    <div data-testid="suppliers-page">
      <PageHeader title="EUDR suppliers" subtitle="Vendors with PO lines for In scope or Check materials (placeholder accounts excluded). Invite them to the EP Group supplier portal and review their submissions.">
        <Button variant="outline" data-testid="contacts-template-btn" onClick={() => download("/suppliers/contacts/template", "supplier_contacts_template.csv")}><Download className="mr-1.5 h-4 w-4" />Contacts template</Button>
        {can.upload && <Button variant="outline" data-testid="contacts-import-btn" onClick={() => fileRef.current.click()}><UploadCloud className="mr-1.5 h-4 w-4" />Import contacts</Button>}
        <input ref={fileRef} type="file" accept=".csv,.xlsx" className="hidden" data-testid="contacts-import-input" onChange={(e) => { const f = e.target.files[0]; e.target.value = ""; if (f) importContacts(f); }} />
        {can.upload && <MillCountryImport onDone={() => setReload((x) => x + 1)} />}
        {can.upload && <RefReqBatch onDone={() => setReload((x) => x + 1)} />}
        {can.upload && <ProdReqBatch onDone={() => setReload((x) => x + 1)} />}
        {can.upload && <Button variant="outline" data-testid="retry-failed-emails-btn" disabled={busy} onClick={retry}><RotateCw className="mr-1.5 h-4 w-4" />Retry failed emails</Button>}
        {can.admin && <Button variant="outline" data-testid="patterns-btn" onClick={() => setPatterns(true)}><Filter className="mr-1.5 h-4 w-4" />Exclusions</Button>}
      </PageHeader>
      <ActionBanner action={action} clearTo="/suppliers" />
      <PatternsDialog open={patterns} onClose={() => setPatterns(false)} onSaved={() => setReload((x) => x + 1)} />
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <div className="relative w-full max-w-xs">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input data-testid="suppliers-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search vendor or name…" className="h-9 bg-white pl-9" />
        </div>
        <Input data-testid="suppliers-country-filter" value={country} onChange={(e) => setCountry(e.target.value)} placeholder="Country (CN)" maxLength={2} className="h-9 w-32 bg-white uppercase" />
        <Select value={commodity} onValueChange={setCommodity}>
          <SelectTrigger className="h-9 w-40 bg-white" data-testid="suppliers-commodity-filter"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">All commodities</SelectItem>{COMMODITIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="h-9 w-44 bg-white" data-testid="suppliers-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">All statuses</SelectItem>{Object.entries(SUP_STATUS).map(([k, v]) => <SelectItem key={k} value={k}>{v[0]}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={risk} onValueChange={setRisk}>
          <SelectTrigger className="h-9 w-40 bg-white" data-testid="suppliers-risk-filter"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">Any country risk</SelectItem><SelectItem value="none">Risk not set</SelectItem><SelectItem value="low">Low</SelectItem><SelectItem value="standard">Standard</SelectItem><SelectItem value="high">High</SelectItem></SelectContent>
        </Select>
        <Select value={mill} onValueChange={setMill}>
          <SelectTrigger className="h-9 w-48 bg-white" data-testid="suppliers-mill-filter"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">Any mill country</SelectItem><SelectItem value="eu">Mill in the EU</SelectItem><SelectItem value="non_eu">Mill outside the EU</SelectItem><SelectItem value="differs">Mill ≠ vendor country</SelectItem></SelectContent>
        </Select>
        <Select value={refreq} onValueChange={setRefreq}>
          <SelectTrigger className="h-9 w-52 bg-white" data-testid="suppliers-refreq-filter"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">Any reference request</SelectItem><SelectItem value="sent">Reference request sent</SelectItem><SelectItem value="not_sent">Reference request not sent</SelectItem><SelectItem value="received">References received</SelectItem></SelectContent>
        </Select>
        <Select value={prodreq} onValueChange={setProdreq}>
          <SelectTrigger className="h-9 w-56 bg-white" data-testid="suppliers-prodreq-filter"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">Any product data request</SelectItem><SelectItem value="sent">Product data request sent</SelectItem><SelectItem value="not_sent">Product data request not sent</SelectItem><SelectItem value="failed">Product data request failed</SelectItem></SelectContent>
        </Select>
        <label className="flex items-center gap-2 text-xs text-slate-600"><Switch checked={ic} onCheckedChange={setIc} data-testid="suppliers-intercompany-toggle" />Intercompany (EP Group UK) only</label>
        <span className="text-xs text-slate-400">Plant filter: global selector</span>
      </div>
      {can.upload && sel.length > 0 && (
        <div className="mb-3 flex flex-wrap items-center gap-3 rounded-xl border border-[#D52B3B]/30 bg-[#D52B3B]/5 px-4 py-2.5 fade-up" data-testid="batch-bar">
          <span className="text-sm font-medium">{sel.length} selected</span>
          <label className="flex items-center gap-2 text-sm"><Switch checked={auto} onCheckedChange={setAuto} data-testid="batch-auto-remind" /> Auto-remind every 7 days (max 3)</label>
          <Button size="sm" data-testid="batch-invite-btn" disabled={busy} onClick={batch}>{busy ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Send className="mr-1.5 h-4 w-4" />}Batch invite</Button>
          <Button size="sm" variant="outline" data-testid="export-links-btn" onClick={() => download(`/suppliers/links.csv?ids=${sel.join(",")}`, "supplier_links.csv")}><Download className="mr-1.5 h-4 w-4" />Export links</Button>
          <button className="ml-auto text-xs text-slate-500 hover:underline" onClick={() => setSel([])}>Clear</button>
        </div>
      )}
      <DataTable endpoint="/suppliers" params={params} columns={columns} testid="suppliers-table" reloadKey={reload} onRowClick={(r) => nav(`/suppliers/${r.id}`)} />
      <BatchResult res={res} onClose={() => setRes(null)} />
    </div>
  );
}
